import { actionEmitter, EmitEvent } from "../config/ActionEmitter";
import { ComponentToSectionCommand } from "../models/content/ComponentToSectionCommand";
import { AddUpdateSectionCommand } from "../models/content/AddUpdateSectionCommand";
import { MoveColumnComponentCommand } from "../models/content/MoveColumnComponentCommand";
import { SaveContentComponentCommand } from "../models/content/SaveContentComponentCommand";
import { IApiResponse, IComponent, IContentComponent, IPage, IComponentProperty, IScriptAndStyle, IVersion, IVersionSection, IComponentSwitchResult } from "../models/Interfaces";
import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { ScriptIncludeType } from "../config/Enums";
import { UpdateComponentValuesCommand } from "../models/content/UpdateComponentValuesCommand";
import { BaseService } from "./BaseService";

export class ContentEditorService extends BaseService {

  public GetComponentPreview = async (apitag: string, version: number, aspage: boolean = true) : Promise<string> => {
    if (this.siteKey) {

      const url = `https://sbsites.azurewebsites.net/Api/GetComponentHTML/${this.siteKey}/${apitag}/${version}?editor=true`;

      const res = await axios.get(url, {});

      if (res && res.data) {
        return res.data;
      }
    }
    return "";
  }

  public GetScriptsAndCss = async (include: ScriptIncludeType) : Promise<Array<IScriptAndStyle> | null> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/GetScriptsCss/${this.siteKey}/${include}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        return res.ReturnData as Array<IScriptAndStyle>;
      } else {
        console.error("Failed to retrieve site scripts/styles");
      }
    }
    return null;
  }

  public SaveSection = async (section: IVersionSection) : Promise<IVersionSection | null> => {

    if (section && this.siteKey) {
      const cmd = new AddUpdateSectionCommand();
      cmd.Id = section.Id;
      cmd.SiteId = this.siteKey;
      cmd.BackgroundColour = section.BackgroundColour ?? "";
      cmd.BackgroundImage = section.BackgroundImage ?? "";
      cmd.ClientJSScript = section.ClientScript ?? "";
      cmd.Columns = section.Columns;
      cmd.Name = uuidv4();
      cmd.Order = section.Order;
      cmd.PageVersionId = section.PageVersionId;
      cmd.SectionClass = section.SectionClass ?? "";
      cmd.SectionHRefId = section.SectionHRefId ?? "";
      cmd.Template = section.Template 
      
      const url = '/SiteAdmin/SaveSection';
      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200) {
        const saved: IVersionSection = res.ReturnData ?? section;
        // Creation can append on the server; use the same reorder endpoint as the section arrows.
        if (section.Id === 0 && saved.Id > 0) {
          const targetOrder = Math.max(1, section.Order);
          const positioned = await this.ReIndexSection(saved.Id, targetOrder);
          if (positioned) {
            saved.Order = targetOrder;
          } else {
            actionEmitter.emit(EmitEvent.Result, false, "Section added, but it could not be moved to the requested position. Use the section arrows to move it.");
            this.notifyLayoutChanged();
            return saved;
          }
        }
        actionEmitter.emit(EmitEvent.Result, true, section.Id === 0 ? "Section added" : "Section saved");
        this.notifyLayoutChanged();
        return saved;
      } else {
        this.EmitDataError(res, "Failed to save section details");
      }
    }
    return null;
  }

  public DeleteSection = async (sectionid: number) : Promise<boolean> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/DeleteSection/${this.siteKey}/${sectionid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200) {
        actionEmitter.emit(EmitEvent.Result, true, res?.Messages?.[0] ?? "Section and content deleted");
        this.notifyLayoutChanged();
        return true;
      } else {
        this.EmitDataError(res, "Failed to remove section and content");
      }
    }
    return false;
  }

  public ReIndexSection = async (sectionid: number, newindex: number): Promise<boolean> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/ReIndexSection/${this.siteKey}/${sectionid}/${newindex}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        this.notifyLayoutChanged();
        return true;
      } else {
        this.EmitDataError(res, "Failed to reorder section");
      }
    }

    return false;
  }

  public AddCComponentToColumnSection = async (component: IComponent, sectionid: number, colindex: number): Promise<IContentComponent | null> => {
    if (component && sectionid && this.siteKey) {
      const url = "/SiteAdmin/AddComponentToSection";
      const cmd = new ComponentToSectionCommand();
      cmd.SiteId = this.siteKey;
      cmd.ColIndex = colindex;
      cmd.SectionId = sectionid;
      cmd.ComponentId = component.Id;
      
      if (component.Properties) {
        cmd.PropertyValues = component.Properties.map((p: IComponentProperty) => {
          return { PropertyId: p.Id, Value: p.DefaultValue };
        });
      }
      
      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        this.notifyLayoutChanged();
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Unable to save component details");
      }
    }
    return null;
  }

  public AddComponentToGridSection = async (sectionid: number, component: IComponent, x: number, y: number, w: number, h: number): Promise<IContentComponent | null> => {
    if (this.siteKey && component) {
      const url = "/SiteAdmin/AddComponentToSection";
      const cmd = new ComponentToSectionCommand();
      cmd.SiteId = this.siteKey;
      cmd.SectionId = sectionid;
      cmd.ComponentId = component.Id;
      cmd.PosX = x;
      cmd.PosY = y;
      cmd.Width = w;
      cmd.Height = h;
      cmd.ColIndex = 0;

      if (component.Properties) {
        cmd.PropertyValues = component.Properties.map((p: IComponentProperty) => {
          let defvalue: string;

          if (p.Type.Name === "SelectOptions") {
            defvalue = p.DefaultValue?.split("|")?.[0] ?? "";
          } else {
            defvalue = p.DefaultValue;
          }
          return { PropertyId: p.Id, Value: defvalue };
        });
      }

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        this.notifyLayoutChanged();
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to add component");
      }
    }
    return null;
  }

  public MoveGridComponent = async (sectionid: number, componentid: number, x: number, y: number, w: number, h: number): Promise<boolean> => {
    if (this.siteKey) {
      const url = `/SiteAdmin/ResizeComponent`;
      const cmd = new ComponentToSectionCommand();
      cmd.SiteId = this.siteKey;
      cmd.ContentComponentId = componentid;
      cmd.SectionId = sectionid;
      cmd.PosX = x;
      cmd.PosY = y;
      cmd.Height = h;
      cmd.Width = w;

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200) {
        this.notifyLayoutChanged();
        return true;
      } else {
        this.EmitDataError(res, "Failed to move component");
      }
    }
    return false;
  }

  public DeleteContentComponent = async (componentid: number) : Promise<boolean> => {
    if (componentid && this.siteKey) {
      const url = `/SiteAdmin/DeleteContentComponent/${this.siteKey}/${componentid}`;

      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        this.notifyLayoutChanged();
        return true;
      } else {
        this.EmitDataError(res, "Unable to remove component");        
      }
    }
    return false;
  }

  public MoveContentComponent = async (componentid: number, newcolindex: number) : Promise<boolean> => {
    if (this.siteKey) {
      const cmd = new MoveColumnComponentCommand();
      cmd.SiteId = this.siteKey;
      cmd.ComponentId = componentid;
      cmd.ColIndex = newcolindex;

      const url = '/SiteAdmin/MoveComponentColumn';

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200) {
        this.notifyLayoutChanged();
        return true;
      } else {
        this.EmitDataError(res, "Failed to move component to new position");
      }
    }
    return false;
  }

  public SwitchColumnComponents = async (sourcecompid: number, targetcompid: number) : Promise<IComponentSwitchResult | null> => {
    if (this.siteKey && sourcecompid && targetcompid) {
      const cmd = {
        SourceComponentId: sourcecompid,
        TargetComponentId: targetcompid
      };
      const url: string = "/SiteAdmin/SwitchComponentColumn";
      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);
      if (res?.StatusCode === 200 && res.ReturnData) {
        this.notifyLayoutChanged();
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to switch components");
      }
    }
    return null;
  }

  public SaveContentComponent = async (component: IContentComponent) : Promise<IContentComponent | null> => {
    if (this.siteKey && component) {
      const url = '/SiteAdmin/SaveContentComponent';
      const cmd = new SaveContentComponentCommand();
      cmd.ContentComponent = {
        ...component,
        ApiTag: component.ApiTag ?? "",
        ParentPageUrl: component.ParentPageUrl ?? "",
        PublishedDate: component.PublishedDate ?? new Date().toISOString(),
        BaseComponent: {
          ...component.BaseComponent,
          Name: component.BaseComponent.Name ?? "",
          DisplayName: component.BaseComponent.DisplayName ?? "",
          ExternalSource: component.BaseComponent.ExternalSource ?? "",
          Properties: component.BaseComponent.Properties.map((prop) => ({
            ...prop,
            Component: prop.Component
              ? {
                  ...prop.Component,
                  Name: prop.Component.Name ?? "",
                  DisplayName: prop.Component.DisplayName ?? "",
                  ExternalSource: prop.Component.ExternalSource ?? "",
                  Properties: prop.Component.Properties ?? []
                }
              : {
                  Id: 0,
                  Name: "",
                  DisplayName: "",
                  ExternalData: false,
                  ExternalSource: "",
                  ExternalType: null,
                  ExternalTypeDescription: "",
                  Properties: []
                },
            PropertyName: prop.PropertyName ?? "",
            DisplayLabel: prop.DisplayLabel ?? "",
            DefaultValue: prop.DefaultValue ?? "",
            ValidatorRegEx: prop.ValidatorRegEx ?? "",
            MapFromPropertyName: prop.MapFromPropertyName ?? "",
            PublishedValue: prop.PublishedValue ?? "",
            ValidationErrorMessage: prop.ValidationErrorMessage ?? ""
          }))
        }
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        this.notifyLayoutChanged();
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save content details");
      }
    }
    return null;
  }

  public UpdateComponentValues = async (component: IContentComponent) : Promise<IContentComponent | null> => {
    if (this.siteKey && component) {
      const url = 'SiteAdmin/UpdateComponentValues';
      const cmd = new UpdateComponentValuesCommand();
      cmd.PageVersionSectionId = component.SectionId;
      cmd.ContentComponentId = component.Id;
      cmd.ColSpan = component.ColSpan;
      cmd.SiteId = this.siteKey;

      component.BaseComponent.Properties.forEach(prop => {
        cmd.AddProperyValue(prop.PublishedPropertyValueId, prop.Id, prop.PublishedValue ?? "");
      });

      const res = await this._ajaxService.Post<IApiResponse>(url, cmd, true);

      if (res?.StatusCode === 200 && res?.ReturnData) {
        this.notifyLayoutChanged();
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save content details");
      }
    }
    return null;
  }

  private notifyLayoutChanged(): void {
    actionEmitter.emit(EmitEvent.LayoutChanged, true, "layout changed");
  }
}
